// Support, Privacy and Terms (the legacy text, unchanged). Reachable without signing in.
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../components/CustomerLayout';

export function SupportPage() {
  usePageTitle('Support');
  return (
    <>
      <h1 className="qcw-greeting">Support</h1>
      <p className="qcw-subgreeting">Common questions, and how to reach us for anything else.</p>

      <section className="qcw-section" aria-label="Self-service">
        <h2 className="qcw-section-title">Things you can do yourself, right now</h2>
        <div className="qcw-section-body">
          <ul className="qcw-support-list">
            <li>
              <strong>Card lost or stolen?</strong>
              <span>
                Go to your card account and select &quot;Report lost.&quot; This closes the card immediately and orders
                a replacement.
              </span>
            </li>
            <li>
              <strong>Need to lock your card temporarily?</strong>
              <span>
                Use &quot;Lock card&quot; in Card Settings. No new purchases go through while the card is locked.
              </span>
            </li>
            <li>
              <strong>Forgot your password?</strong>
              <span>
                Select &quot;Forgot password?&quot; on the sign-in page. We&apos;ll email you a verification code to
                reset it.
              </span>
            </li>
            <li>
              <strong>Need to update your contact details?</strong>
              <span>Visit the Profile page to update your information.</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="qcw-section" aria-label="Contact us">
        <h2 className="qcw-section-title">Still need help?</h2>
        <div className="qcw-section-body">
          <p className="qcw-section-hint">Email us and we&apos;ll get back to you as soon as possible.</p>
          <p>
            <a href="mailto:support@qorteams.com">support@qorteams.com</a>
          </p>
        </div>
      </section>
    </>
  );
}

function LegalDoc({ title, children }: { title: string; children: ReactNode }) {
  usePageTitle(title);
  return (
    <>
      <h1 className="qcw-greeting">{title}</h1>
      <p className="qcw-subgreeting">Last updated: September 2026</p>
      <section className="qcw-section" aria-label={title}>
        <div className="qcw-section-body qcw-legal-doc">{children}</div>
      </section>
    </>
  );
}

export function PrivacyPage() {
  return (
    <LegalDoc title="Privacy Policy">
      <p>
        This Privacy Policy explains how QOR Platform (&quot;QOR,&quot; &quot;we,&quot; &quot;us&quot;) collects, uses,
        and protects information about cardholders who use this portal to manage a prepaid card issued through one of
        our program partners.
      </p>

      <h3>Information we collect</h3>
      <p>
        To open and maintain a card account, we collect information you provide directly (name, email, phone number, and
        other identity details required by our card-issuing partners) and information generated by your use of the card,
        such as transaction history, balance, and card status. We do not collect or store your full card number,
        expiration date, or CVV on this platform -- that data is held exclusively by our card-issuing partners under
        their own security controls.
      </p>

      <h3>How we use your information</h3>
      <p>
        We use your information to open and service your card account, process loads and transactions, verify your
        identity as required by our issuing partners&apos; compliance obligations, communicate with you about your
        account (including security notices and password resets), and maintain records required for regulatory and audit
        purposes.
      </p>

      <h3>Who we share it with</h3>
      <p>
        We share the minimum information necessary with the card-issuing partner backing your specific card program, and
        with service providers who help us operate the platform (such as email delivery). We do not sell your personal
        information. We may disclose information where required by law or to a card network/issuing bank as part of
        standard compliance review.
      </p>

      <h3>How we protect your information</h3>
      <p>
        Access to cardholder data within QOR is restricted to authorized program staff, every administrative action is
        logged, and account access requires authentication. Passwords are never stored in plain text. Card-level
        security actions -- freezing a card or reporting it lost or stolen -- are available to you directly in this
        portal.
      </p>

      <h3>Your choices</h3>
      <p>
        You can review and update your profile information from the Profile page, and you can freeze your card or report
        it lost or stolen at any time from your card account page. To request a copy of your data or ask us to delete
        it, contact us using the information on the Support page.
      </p>

      <h3>Contact us</h3>
      <p>
        Questions about this policy can be sent to the contact listed on our <Link to="/support">Support page</Link>.
      </p>
    </LegalDoc>
  );
}

export function TermsPage() {
  return (
    <LegalDoc title="Terms of Service">
      <p>
        These Terms of Service (&quot;Terms&quot;) govern your use of this portal to access and manage a prepaid card
        account. By signing in, you agree to these Terms.
      </p>

      <h3>Your account</h3>
      <p>
        Your card program is administered by QOR Platform on behalf of a card-issuing partner. You&apos;re responsible
        for keeping your login credentials confidential and for all activity that occurs under your account. If you
        believe your account has been accessed without your permission, change your password immediately and contact us.
      </p>

      <h3>Your card</h3>
      <p>
        The card associated with your account is issued by our card-issuing partner and is subject to that
        partner&apos;s own cardholder agreement, which governs the terms under which funds are held and transactions are
        processed. This portal is how you view and manage that card -- it does not itself issue funds or extend credit.
      </p>

      <h3>Acceptable use</h3>
      <p>
        You agree to use this portal only for its intended purpose: viewing your card account and managing your own
        card. You agree not to attempt to access another cardholder&apos;s account, interfere with the platform&apos;s
        operation, or use the portal for any unlawful purpose.
      </p>

      <h3>Lost, stolen, or compromised cards</h3>
      <p>
        If your card is lost, stolen, or you suspect unauthorized use, report it immediately using the &quot;Report
        lost&quot; option on your card account page. This closes the card and orders a replacement.
      </p>

      <h3>Changes to these Terms</h3>
      <p>
        We may update these Terms from time to time. Continued use of the portal after an update constitutes acceptance
        of the revised Terms.
      </p>

      <h3>Contact us</h3>
      <p>
        Questions about these Terms can be sent to the contact listed on our <Link to="/support">Support page</Link>.
      </p>
    </LegalDoc>
  );
}
